<script setup>
import { ref, computed, nextTick } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconArrowRight from '@/components/icon/IconArrowRight.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconCheckmarkStroke from '@/components/icon/IconCheckmarkStroke.vue'

/**
 * The slim column between Preview and Program (OBS studio-mode transition
 * panel): Cut / Fade, the fade duration and the one primary action — an
 * arrow button whose tooltip names it (Start with this scene / Send to live).
 * State lives on the desk so the pop-out and the main window agree. Below lg
 * it lays out as a row under Preview.
 */
const props = defineProps({
	desk: { type: Object, required: true },
	sessionActive: { type: Boolean, default: false },
	/** Why Send to live is disabled; empty when usable. */
	disabledReason: { type: String, default: '' },
	enabled: { type: Boolean, default: true },
})

const emit = defineEmits(['send'])

const TRANSITIONS = ['Cut', 'Fade']
const PRESETS = [250, 500, 1000]
const MIN_MS = 50
const MAX_MS = 10000
const durationMenu = ref(null)
const custom = ref('')
const customInvalid = ref(false)

const type = computed(() => props.desk.transitionType.value)
const duration = computed(() => parseInt(props.desk.transitionDuration.value, 10) || 300)
const durationLabel = computed(() => (duration.value >= 1000 && duration.value % 100 === 0 ? `${duration.value / 1000}s` : `${duration.value} ms`))
const disabled = computed(() => !!props.disabledReason)
const sending = computed(() => !!props.desk.sending.value)
const actionLabel = computed(() => (props.sessionActive ? 'Send to live' : 'Start with this scene'))
const tooltip = computed(() => {
	if (disabled.value) return props.disabledReason
	return props.sessionActive ? 'Send to live (Space)' : 'Start with this scene (Space)'
})

const setType = (t) => { props.desk.transitionType.value = t }
const msLabel = (ms) => (ms >= 1000 ? `${ms / 1000}s` : `${ms} ms`)

const setDuration = (ms) => {
	props.desk.transitionDuration.value = Math.max(MIN_MS, Math.min(MAX_MS, ms))
	durationMenu.value?.hide?.()
	durationMenu.value?.$el?.querySelector?.('button')?.focus({ preventScroll: true })
}
const applyCustom = () => {
	const raw = String(custom.value ?? '').trim()
	if (!raw) return
	const n = Number(raw)
	if (!Number.isInteger(n) || n < MIN_MS || n > MAX_MS) {
		customInvalid.value = true
		return
	}
	setDuration(n)
	custom.value = ''
}

const menuHead = ref(null)
const menuFocusables = () => [...(menuHead.value?.closest('ul')?.querySelectorAll('[data-duration-option] button, input') || [])]
const focusSelected = async () => {
	await nextTick()
	const items = menuFocusables()
	const current = items.find((b) => b.closest(`[data-duration-option="${duration.value}"]`))
	;(current || items[0])?.focus({ preventScroll: true })
}
const onMenuVisibility = (open) => {
	if (open) focusSelected()
	else { custom.value = ''; customInvalid.value = false }
}
// Enter/Space open the menu (the trigger is a native button); arrows open it too.
const onTriggerKeydown = (e) => {
	if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
	e.preventDefault()
	durationMenu.value?.show?.()
	focusSelected()
}
const onMenuKeydown = (e) => {
	if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
	if ((e.key === 'Home' || e.key === 'End') && e.target.tagName === 'INPUT') return
	const items = menuFocusables()
	if (!items.length) return
	e.preventDefault()
	const i = items.indexOf(document.activeElement)
	const next = e.key === 'Home' ? 0
		: e.key === 'End' ? items.length - 1
			: e.key === 'ArrowDown' ? (i + 1) % items.length
				: (i - 1 + items.length) % items.length
	items[next].focus({ preventScroll: true })
}
</script>

<template>
	<div
		class="transition-rail flex min-w-0 flex-row items-center justify-center gap-2.5 rounded-xl bg-surface-2 p-2 ring-1 ring-surface-3 lg:h-full lg:flex-col lg:items-stretch lg:justify-center"
		role="toolbar"
		aria-label="Transition and Send to live"
		data-testid="transition-rail"
		:data-transition="type"
	>
		<span class="hidden text-center text-[0.6rem] font-medium uppercase tracking-wider text-surface-7 xl:block">Transition</span>

		<!-- Cut | Fade: one two-option segmented toggle -->
		<div class="grid h-9 w-[112px] grid-cols-2 gap-0.5 rounded-lg bg-surface-1 p-0.5 ring-1 ring-surface-3 lg:w-full" role="radiogroup" aria-label="Transition">
			<button
				v-for="t in TRANSITIONS"
				:key="t"
				type="button"
				role="radio"
				:aria-checked="type === t"
				class="h-full min-w-0 rounded-md text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-not-allowed disabled:opacity-50"
				:class="type === t ? 'bg-surface-4 text-white' : 'text-surface-7 hover:bg-surface-3 hover:text-surface-text-2'"
				:disabled="!enabled"
				:data-testid="`transition-${t.toLowerCase()}`"
				@click="setType(t)"
			>
				{{ t }}
			</button>
		</div>

		<!-- Fade duration (inert for Cut) -->
		<Tooltip :active="type === 'Cut'" content="Cut switches instantly" wrapper-class="block w-[112px] lg:w-full" wrap-trigger>
			<DropDownMenu
				ref="durationMenu"
				type="custom"
				size="sm"
				position="center"
				use-teleport
				max-height="none"
				:disabled="!enabled || type === 'Cut'"
				aria-label="Fade duration"
				list-width="w-44 max-w-[calc(100vw-16px)]"
				list-classes="!py-1"
				:custom-classes="`inline-flex h-9 w-full min-w-0 items-center justify-between gap-1 rounded-lg bg-surface-1 px-2.5 text-xs font-normal tabular-nums ring-1 ${!enabled || type === 'Cut' ? 'pointer-events-none text-surface-6 ring-surface-3' : 'text-surface-text-1 ring-surface-3 hover:text-white hover:ring-surface-5'}`"
				data-testid="transition-duration"
				@keydown="onTriggerKeydown"
				@visibility-change="onMenuVisibility"
			>
				<template #toggle-button>
					<span class="min-w-0 truncate">{{ type === 'Cut' ? '—' : durationLabel }}</span>
					<icon-base width="9" height="9" fill="none" class="shrink-0"><icon-chevron-down /></icon-base>
				</template>
				<template #menu-items>
					<li ref="menuHead" class="px-2.5 pb-1 pt-1.5 text-[0.65rem] font-medium uppercase tracking-wider text-surface-6">Fade duration</li>
					<DropDownMenuItem
						v-for="ms in PRESETS"
						:key="ms"
						:data-duration-option="ms"
						:data-testid="`transition-duration-${ms}`"
						:data-selected="duration === ms || undefined"
						custom-classes="flex h-8 w-full items-center gap-2 rounded-md px-2.5 text-xs tabular-nums text-surface-text-1 hover:bg-surface-3 hover:text-white focus:outline-none focus-visible:bg-surface-3 focus-visible:text-white"
						class="px-1"
						@keydown="onMenuKeydown"
						@click="setDuration(ms)"
					>
						<span class="flex w-3 shrink-0 justify-center text-main-light" aria-hidden="true">
							<icon-base v-if="duration === ms" width="12" height="12" fill="none"><icon-checkmark-stroke /></icon-base>
						</span>
						{{ msLabel(ms) }}
						<span v-if="duration === ms" class="sr-only">(selected)</span>
					</DropDownMenuItem>
					<li class="flex items-center gap-1.5 px-2 pb-1 pt-1.5" @click.stop @keydown="onMenuKeydown">
						<input
							v-model="custom"
							type="number"
							:min="MIN_MS"
							:max="MAX_MS"
							step="50"
							:placeholder="String(duration)"
							class="h-7 w-full min-w-0 rounded-md border-0 bg-surface-1 px-2 text-xs tabular-nums text-surface-text-2 ring-1 ring-inset placeholder:text-surface-6 focus:outline-none focus:ring-main"
							:class="customInvalid ? 'ring-error' : 'ring-surface-4 hover:ring-surface-6'"
							:aria-invalid="customInvalid || undefined"
							:title="`${MIN_MS}–${MAX_MS} ms`"
							aria-label="Custom fade duration in milliseconds"
							data-testid="transition-duration-custom"
							@input="customInvalid = false"
							@keydown.enter.prevent="applyCustom"
						>
						<FormButton type="secondary" size="sm" class="shrink-0 !px-2" data-testid="transition-duration-custom-apply" @click="applyCustom">ms</FormButton>
					</li>
				</template>
			</DropDownMenu>
		</Tooltip>

		<!-- The go button: icon only, the tooltip names the action -->
		<Tooltip :content="tooltip" wrapper-class="block w-[112px] lg:w-full" wrap-trigger popper-class="max-w-[240px] text-sm break-words">
			<button
				type="button"
				class="send-live-button flex h-9 w-full items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-surface-2"
				:class="disabled ? 'cursor-not-allowed bg-surface-1 text-surface-5 ring-1 ring-inset ring-surface-3' : sending ? 'cursor-progress bg-main text-white' : 'bg-main text-white hover:bg-main-light active:translate-y-px'"
				:disabled="disabled || sending"
				:aria-label="actionLabel"
				:aria-busy="sending || undefined"
				data-testid="send-to-live-main"
				:data-state="disabled ? 'disabled' : 'ready'"
				@click="emit('send')"
			>
				<icon-base width="20" height="20" :width-view-box="24" :height-view-box="24" fill="none" :class="sending ? 'animate-pulse' : ''"><icon-arrow-right /></icon-base>
			</button>
		</Tooltip>
	</div>
</template>
